import * as React from "react";
import {
  DashboardSectionContainer,
  setInitialMonthsYears,
  filterCommissionSubset,
  formatBarData,
  combineCommissionsAll,
} from "../../../libs/commission_calculator_dashboard";
import { DonutChart } from "./DonutChart";
import { ChartHeader } from "./ChartHeader";
import { useEffect, useState } from "react";
import { DateRange } from "./DateRange";
import * as moment from "moment";
import Select from "react-select";
import styled from "styled-components";
import Loader from "../../core/Loader";
import { getCommissionPercentile } from "../../../libs/commission_calculator";
import { toast } from "../../../utils";
import * as Sentry from "@sentry/browser";

interface IProps {
  commissionsAll?: any;
}

const DashboardPercentileSection = ({ commissionsAll }: IProps) => {
  const [userOptions, setUserOptions] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);

  const [monthSelectedStart, setMonthSelectedStart] = useState(
    moment()
      .subtract(1, "month")
      .toDate()
  );

  const [monthSelectedEnd, setMonthSelectedEnd] = useState(moment().toDate());

  const [yearOptions, setYearOptions] = useState(null);

  const [yearSelected, setYearSelected] = useState(null);

  const [currentPercentile, setCurrentPercentile] = useState(null);

  const [commissionsSet, setCommissionsSet] = useState(null);

  const [loadingMonths, setLoadingMonths] = useState(true);

  const [dataFound, setDataFound] = useState(false);

  // If commissions are present need to do these things:
  // 1. Set the userOptionsList
  // 2. Set the selected User
  useEffect(() => {
    // Set the user options list for ranking
    if (commissionsAll) {
      const userOptionsList = [];
      commissionsAll.forEach(commission => {
        if (
          !userOptionsList.some(v => v["value"] === commission["user"]["id"])
        ) {
          userOptionsList.push({
            label: commission.user.name,
            value: commission.user.id,
          });
        }
      });
      setUserOptions(userOptionsList);
      if (userOptionsList.length > 1) {
        userOptionsList.unshift({ label: "Team", value: 999999 });
      }
      setSelectedUser(userOptionsList[0]);
    }
  }, [commissionsAll]);

  // If the selectedUser changes, set their commissions into a subset
  useEffect(() => {
    setLoadingMonths(true);
    setDataFound(false);
    setCommissionsSet(null);
  }, [selectedUser]);

  // If commissionsSet is null but there's a selected user and there are commissions
  // then get the commission subset
  useEffect(() => {
    if (commissionsSet == null && selectedUser && commissionsAll) {
      // Set commissions subset, if its team then send all, otherwise filter for the user selected
      const commissionsSubset =
        selectedUser["label"] === "Team" || selectedUser["value"] === 999999
          ? [...commissionsAll]
          : commissionsAll.filter(
              c => c["user"]["id"] == selectedUser["value"]
            );

      if (commissionsAll && commissionsSubset) {
        setCommissionsSet(commissionsSubset);
      }
    }
  }, [commissionsSet, selectedUser, commissionsAll]);

  // Set the start and end date for selectedUser (if the commission subset changes).
  // This is based on the latest commission for the user
  useEffect(() => {
    if (commissionsSet) {
      setInitialMonthsYears({
        commissionsSet,
        setMonthSelectedStart,
        setMonthSelectedEnd,
        setYearOptions,
        setYearSelected,
      });
    }
  }, [commissionsSet]);

  // Process when date range changes:
  // 1. Get the commissions for that user for that selected date range
  // 2. Combine to get the average
  // 3. Send average to backend and get the percentile
  useEffect(() => {
    setDataFound(false);
    setLoadingMonths(true);
    getSetPercentile(
      monthSelectedStart,
      monthSelectedEnd,
      commissionsSet,
      setDataFound,
      setCurrentPercentile,
      setLoadingMonths
    );
  }, [monthSelectedStart, monthSelectedEnd, commissionsSet]);

  return (
    <DashboardSectionContainer>
      <ChartHeader
        title={
          selectedUser
            ? `${selectedUser["label"]}'s Percentile Rank`
            : "Loading"
        }
      >
        <Select
          options={userOptions}
          onChange={(opt: ISelectOption) => setSelectedUser(opt)}
          value={selectedUser}
          clearable={false}
          className={"ReactSelectNamePicker"}
        />
      </ChartHeader>
      {loadingMonths ? (
        <Loader />
      ) : (
        <DonutChartOuterContainer>
          <DonutChartInfoContainer>
            <TitleContainer>Select Range</TitleContainer>
            <DateRange
              monthSelectedStart={monthSelectedStart}
              setMonthSelectedStart={setMonthSelectedStart}
              monthSelectedEnd={monthSelectedEnd}
              setMonthSelectedEnd={setMonthSelectedEnd}
            />
          </DonutChartInfoContainer>
          {dataFound && currentPercentile ? (
            <DonutChartContainer>
              <DonutChart
                percent={
                  currentPercentile
                    ? currentPercentile["quota_average_percentile"]
                    : 0
                }
              />
              <div>
                {" "}
                {"Percentile rank for " +
                  selectedUser["label"] +
                  " against all core users"}{" "}
              </div>
            </DonutChartContainer>
          ) : (
            <MsgContainer> No Data Found </MsgContainer>
          )}
        </DonutChartOuterContainer>
      )}
    </DashboardSectionContainer>
  );
};

// 1. Get the commissions for that user for that selected date range
// 2. Combine to get the average
// 3. Send average to backend and get the percentile
export const getSetPercentile = (
  monthSelectedStart: Date,
  monthSelectedEnd: Date,
  commissionsSet: any,
  setDataFound: React.Dispatch<React.SetStateAction<boolean>>,
  setCurrentPercentile: React.Dispatch<any>,
  setLoadingMonths: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (
    monthSelectedStart &&
    monthSelectedEnd &&
    commissionsSet &&
    Array.isArray(commissionsSet)
  ) {
    // Filter commissions
    const filteredCommissions = filterCommissionSubset(
      commissionsSet,
      monthSelectedStart,
      monthSelectedEnd
    );
    if (filteredCommissions) {
      const formattedDataMonths = formatBarData(filteredCommissions);
      const combinedCommissions = combineCommissionsAll(
        formattedDataMonths,
        false
      );
      // If it exists and isn't an array (just an object) OR
      // if it exists and is an array with a value
      if (
        (combinedCommissions && !Array.isArray(combinedCommissions)) ||
        (combinedCommissions &&
          Array.isArray(combinedCommissions) &&
          combinedCommissions.length > 0)
      ) {
        const { id, userId, name, ...otherProps } = Array.isArray(
          combinedCommissions
        )
          ? combinedCommissions[0]
          : combinedCommissions;
        // Get percentiles from backend
        getCommissionPercentile(
          moment(monthSelectedStart).format(),
          moment(monthSelectedEnd).format(),
          otherProps
        )
          .then(result => {
            setDataFound(true);
            setCurrentPercentile(result);
            setLoadingMonths(false);
          })
          .catch(e => {
            toast.error("Error retrieving percentile");
            Sentry.captureException(e);
            setLoadingMonths(false);
          });
      } else {
        setLoadingMonths(false);
      }
    } else {
      setLoadingMonths(false);
    }
  }
};

const DonutChartOuterContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-evenly;
  border-top: 1px solid #ededed;
`;

const DonutChartInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 10px;
  background-color: #ededed;
`;

const DonutChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0;
`;

const TitleContainer = styled.div`
  padding: 10px 0;
  font-size: 12px;
`;

const MsgContainer = styled.div`
  min-width: 200px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default DashboardPercentileSection;



// WEBPACK FOOTER //
// ./app/javascript/components/commission_calculator/dashboard/DashboardPercentileSection.tsx